import { SNIPPET_HEIGHT_MESSAGE_TYPE } from './sandbox-constants';

/**
 * Resize reporter injected into the iframe document. Serialized via toString()
 * and invoked as an IIFE with the iframe's nonce, so it must be fully
 * self-contained (no closure references other than its parameters).
 *
 * @param nonce - per-iframe id echoed back in the height message
 * @param messageType - postMessage type literal
 */
function snippetResizeReporter(nonce: string, messageType: string): void {
  var report = function () {
    var height = document.documentElement.scrollHeight;
    parent.postMessage({ type: messageType, id: nonce, height: height }, '*');
  };
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(report).observe(document.documentElement);
  }
  window.addEventListener('load', report);
  report();
}

/**
 * Builds the full srcdoc document string for a snippet iframe. The snippet html
 * is placed after the base style block so authored css overrides the defaults.
 * The resize reporter lives in the head, ahead of the user content, so that
 * incomplete/unclosed authored markup (e.g. a lone "<d") cannot swallow the
 * reporter script tag and leak its source as visible text.
 *
 * @param html - raw snippet html
 * @param opts - nonce (echoed in resize messages), optional theme vars for :root,
 *   and optional BCP47 lang tag for the document's html element
 *
 * @returns {string}
 */
export function buildSnippetSrcdoc(
  html: string,
  opts: { nonce: string; theme?: Record<string, string>; lang?: string },
): string {
  const { nonce, theme, lang } = opts;

  const themeDecls = theme
    ? Object.entries(theme)
        .filter(([, value]) => value != null && value !== '')
        .map(([name, value]) => `${name}: ${value};`)
        .join(' ')
    : '';
  const rootRule = themeDecls ? `:root { ${themeDecls} }` : '';

  const reporter =
    '<script>(' +
    snippetResizeReporter.toString() +
    ')(' +
    JSON.stringify(nonce) +
    ', ' +
    JSON.stringify(SNIPPET_HEIGHT_MESSAGE_TYPE) +
    ');</script>';

  const langAttr = lang ? ` lang="${lang.replace(/[^a-zA-Z-]/g, '')}"` : '';

  return [
    `<!DOCTYPE html><html${langAttr}><head><meta charset="utf-8">`,
    '<base target="_blank">',
    `<style>${rootRule} html, body { margin: 0; } `,
    'body { font-family: var(--omq-font-family, inherit); color: var(--omq-text-color, inherit); } ',
    'img, iframe { max-width: 100%; } iframe { border: 0; }</style>',
    reporter,
    '</head><body>',
    html || '',
    '</body></html>',
  ].join('');
}
